import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { candidateV2API } from '@/services/api';
import { toast } from 'sonner';

// ─── Queries ────────────────────────────────────────────────────────────────

export const useCandidatesV2 = (params) =>
  useQuery({
    queryKey: ['candidatesV2', params],
    queryFn: () => candidateV2API.getAll(params),
    keepPreviousData: true,
  });

export const useCandidateV2 = (id) =>
  useQuery({
    queryKey: ['candidateV2', id],
    queryFn: () => candidateV2API.getOne(id),
    enabled: !!id,
  });

export const useCandidateActivitiesV2 = (id, params) =>
  useQuery({
    queryKey: ['candidateActivitiesV2', id, params],
    queryFn: () => candidateV2API.getActivities(id, params),
    enabled: !!id,
  });

export const useCandidateNotesV2 = (id, params) =>
  useQuery({
    queryKey: ['candidateNotesV2', id, params],
    queryFn: () => candidateV2API.getNotes(id, params),
    enabled: !!id,
  });

export const useCandidateStageHistoryV2 = (id, params) =>
  useQuery({
    queryKey: ['candidateStageHistoryV2', id, params],
    queryFn: () => candidateV2API.getStageHistory(id, params),
    enabled: !!id,
  });

export const useCompanyUsersV2 = () =>
  useQuery({
    queryKey: ['companyUsersV2'],
    queryFn: () => candidateV2API.getCompanyUsers(),
  });

// ─── Mutations ───────────────────────────────────────────────────────────────

export const useCreateCandidateV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => candidateV2API.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidatesV2'] });
      toast.success('Candidate created successfully');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to create candidate');
    },
  });
};

export const useUpdateCandidateV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => candidateV2API.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['candidatesV2'] });
      queryClient.invalidateQueries({ queryKey: ['candidateV2', id] });
      toast.success('Candidate updated successfully');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to update candidate');
    },
  });
};

export const useArchiveCandidateV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => candidateV2API.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidatesV2'] });
      toast.success('Candidate archived');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to archive candidate');
    },
  });
};

export const useRestoreCandidateV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => candidateV2API.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidatesV2'] });
      toast.success('Candidate restored');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to restore candidate');
    },
  });
};

export const useMoveCandidateStageV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stage }) => candidateV2API.moveStage(id, stage),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['candidatesV2'] });
      queryClient.invalidateQueries({ queryKey: ['candidateV2', id] });
      queryClient.invalidateQueries({ queryKey: ['candidateStageHistoryV2', id] });
      toast.success('Stage updated');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to update stage');
    },
  });
};

export const useAddCandidateActivityV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => candidateV2API.addActivity(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['candidateActivitiesV2', id] });
      queryClient.invalidateQueries({ queryKey: ['candidateV2', id] });
      toast.success('Activity added');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to add activity');
    },
  });
};

export const useAddCandidateNoteV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => candidateV2API.addNote(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['candidateNotesV2', id] });
      queryClient.invalidateQueries({ queryKey: ['candidateV2', id] });
      toast.success('Note added');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to add note');
    },
  });
};
